/**
 * Global cart functions
 * Reusable functions for adding products to cart across all product cards
 */

window.CartFunctions = {
  /**
   * Add a product to the cart
   * @param {string|number} variantId - The variant ID to add
   * @param {number} quantity - Quantity to add (default: 1)
   * @param {object} properties - Optional line item properties
   * @returns {Promise<object>} - Cart data after adding
   */
  async addToCart(variantId, quantity = 1, properties = {}) {
    if (!variantId) {
      console.error('Variant ID is required');
      return;
    }

    try {
      const formData = {
        id: variantId,
        quantity: quantity
      };

      // Add properties if provided
      if (Object.keys(properties).length > 0) {
        formData.properties = properties;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erreur lors de l\'ajout au panier');
      }

      const data = await response.json();

      // Trigger cart update event
      this.triggerCartUpdate();

      // Open sidecart if available
      this.openSidecart();

      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showMessage(error.message || 'Erreur lors de l\'ajout au panier', 'error');
      throw error;
    }
  },

  /**
   * Update cart quantity
   * @param {number} line - Line item index (1-based)
   * @param {number} quantity - New quantity
   * @returns {Promise<object>} - Updated cart data
   */
  async updateQuantity(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour');
      }

      const cart = await response.json();
      this.triggerCartUpdate();
      return cart;
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showMessage('Erreur lors de la mise à jour', 'error');
      throw error;
    }
  },

  /**
   * Get current cart data
   * @returns {Promise<object>} - Current cart data
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error getting cart:', error);
      throw error;
    }
  },

  /**
   * Open sidecart if available
   */
  openSidecart() {
    const overlay = document.querySelector('#overlay');
    if (overlay) {
      overlay.classList.add('is-open');
      document.body.classList.add('cart-open');
    }
  },

  /**
   * Trigger cart update event for other components to listen to
   */
  triggerCartUpdate() {
    const event = new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { timestamp: Date.now() }
    });
    document.dispatchEvent(event);
  },

  /**
   * Show temporary message
   * @param {string} message - Message text
   * @param {string} type - Message type ('success' or 'error')
   */
  showMessage(message, type = 'success') {
    const messageEl = document.createElement('div');
    messageEl.className = `cart-message cart-message--${type}`;
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? 'var(--Products-Acid-green, #B0F84D)' : 'var(--Products-Heart-red, #F4364C)'};
      color: var(--Secondary-Soft-dark-100, #2B2B2B);
      padding: 12px 18px;
      border-radius: 12px;
      font-family: var(--font-family-paragraph, Inter);
      font-size: 14px;
      font-weight: 500;
      z-index: 10000;
      animation: slideInRight 0.3s ease;
    `;

    document.body.appendChild(messageEl);

    setTimeout(() => {
      messageEl.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => {
        messageEl.remove();
      }, 300);
    }, 3000);
  },

  /**
   * Format money using the active market's currency.
   * @param {number} cents - Amount in cents
   * @returns {string} - Formatted price
   */
  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = (window.Shopify?.locale || 'fr').replace('_', '-');
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  }
};

// Add CSS animations for messages
if (!document.querySelector('#cart-functions-styles')) {
  const style = document.createElement('style');
  style.id = 'cart-functions-styles';
  style.textContent = `
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes slideOutRight {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);
}
